import React from 'react';
import { Key, Link as LinkIcon, Shield, Layers } from 'lucide-react';
import { Badge, StatusDot } from '../../../../components/ui';
import { Panel, PanelHeader } from './PanelHeader.js';

type PermissionState = 'active' | 'pending' | 'expired' | 'revoked';

interface PermissionRow {
  id?: string | number;
  name?: string;
  sub?: string;
  icon?: 'key' | 'shield' | 'stack' | 'link';
  state?: PermissionState;
  expires?: string;
}

interface PermissionsPanelProps {
  grants?: Array<Record<string, unknown>>;
}

const ICON_MAP = {
  key: Key,
  shield: Shield,
  stack: Layers,
  link: LinkIcon,
} as const;

export const PermissionsPanel: React.FC<PermissionsPanelProps> = ({ grants = [] }) => {
  const rows: PermissionRow[] = grants.map((raw) => normaliseGrant(raw));
  return (
    <Panel>
      <PanelHeader title="Permissions & tools" subtitle="Grants, MCP connectors, secrets" />
      {rows.length === 0 ? (
        <div className="px-4 py-6 text-xs text-muted">No grants on file.</div>
      ) : (
        <ul className="divide-y divide-border">
          {rows.map((row, i) => {
            const Icon = ICON_MAP[row.icon ?? 'key'];
            const state = row.state ?? 'active';
            const stateMeta: Record<
              PermissionState,
              { variant: Parameters<typeof Badge>[0]['variant']; label: string }
            > = {
              active: { variant: 'success', label: 'active' },
              pending: { variant: 'warning', label: 'pending' },
              expired: { variant: 'neutral', label: 'expired' },
              revoked: { variant: 'danger', label: 'revoked' },
            };
            return (
              <li key={row.id ?? `${row.name ?? 'grant'}-${i}`} className="flex items-center gap-3 px-4 py-2.5 text-sm">
                <span className="inline-flex h-7 w-7 items-center justify-center rounded-md bg-surface-alt text-muted">
                  <Icon size={12} />
                </span>
                <div className="min-w-0 flex-1">
                  <div className="truncate font-medium text-heading">{row.name ?? '—'}</div>
                  {row.sub && <div className="truncate text-xs text-muted">{row.sub}</div>}
                </div>
                {row.expires && <span className="font-mono text-[11px] text-faint">{row.expires}</span>}
                <Badge
                  variant={stateMeta[state].variant}
                  icon={
                    <StatusDot
                      variant={
                        stateMeta[state].variant === 'neutral'
                          ? 'neutral'
                          : stateMeta[state].variant === 'danger'
                            ? 'danger'
                            : stateMeta[state].variant === 'warning'
                              ? 'warning'
                              : 'success'
                      }
                      size="sm"
                    />
                  }
                >
                  {stateMeta[state].label}
                </Badge>
              </li>
            );
          })}
        </ul>
      )}
    </Panel>
  );
};

function normaliseGrant(raw: Record<string, unknown>): PermissionRow {
  const action = typeof raw.action === 'string' ? raw.action : '';
  const resourceType = typeof raw.resourceType === 'string' ? raw.resourceType : '';
  const resourceId = typeof raw.resourceId === 'string' ? raw.resourceId : '';
  const reason = typeof raw.reason === 'string' ? raw.reason : '';
  const decision = typeof raw.decision === 'string' ? raw.decision : '';
  const grantMode = typeof raw.grantMode === 'string' ? raw.grantMode : '';
  const expiresAt = typeof raw.expiresAt === 'string' ? raw.expiresAt : '';
  const revokedAt = typeof raw.revokedAt === 'string' ? raw.revokedAt : '';

  const iconKind: PermissionRow['icon'] = resourceType.toLowerCase().includes('secret')
    ? 'shield'
    : resourceType.toLowerCase().includes('container')
      ? 'stack'
      : resourceType.toLowerCase().includes('net') || resourceType.toLowerCase().includes('http')
        ? 'link'
        : 'key';

  let state: PermissionState = 'active';
  if (revokedAt) state = 'revoked';
  else if (decision === 'deny') state = 'pending';
  else if (expiresAt && new Date(expiresAt).getTime() < Date.now()) state = 'expired';

  return {
    id: typeof raw.id === 'number' || typeof raw.id === 'string' ? (raw.id as string | number) : undefined,
    name: `${resourceType || 'Permission'}${resourceId ? ` · ${resourceId}` : action ? ` · ${action}` : ''}`,
    sub: `${grantMode || 'allow'}${reason ? ` · ${reason}` : ''}`,
    icon: iconKind,
    state,
    expires: expiresAt ? new Date(expiresAt).toLocaleDateString() : '—',
  };
}
